import "./CreateSupabaseProjectComponent.scss";
import {Button} from "@/components/ui/button.tsx";
import FormInput from "@/components/form-components/form-input.tsx";
import * as Yup from "yup";
import {Field, Form, Formik, FormikHelpers} from "formik";
import {motion} from "framer-motion";
import {CommonService} from "@/services";
import {buttonVariants, containerVariants} from "@/constants/animations.ts";
import {useCallback} from "react";
import {SelectContent,Select, SelectGroup, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import FormikPasswordInput from "@/components/form-components/formik-password-input.tsx";
import {useAppDispatch} from "@/store/hooks.ts";
import {setProjectCode} from "@/store/actions/projectdata.action.ts";

interface CreateSupabaseProjectValues {
    project_name: string;
    db_password: string;
    region: string;
}

export interface createProjectValues {
    organization_id?: string;
    onCreate?: (project: any) => void;
    onClose?: () => void;
}

const SUPABASE_REGIONS = [
    { label: "West US (North California)", value: "us-west-1" },
    { label: "East US (North Virginia)", value: "us-east-1" },
    { label: "East US (Ohio)", value: "us-east-2" },
    { label: "Canada (Central)", value: "ca-central-1" },
    { label: "West EU (Ireland)", value: "eu-west-1" },
    { label: "West Europe (London)", value: "eu-west-2" },
    { label: "Central EU (Frankfurt)", value: "eu-central-1" },
    { label: "Central Europe (Zurich)", value: "eu-central-2" },
    { label: "North EU (Stockholm)", value: "eu-north-1" },
    { label: "South Asia (Mumbai)", value: "ap-south-1" },
    { label: "Southeast Asia (Singapore)", value: "ap-southeast-1" },
    { label: "Northeast Asia (Tokyo)", value: "ap-northeast-1" },
    { label: "Northeast Asia (Seoul)", value: "ap-northeast-2" },
    { label: "Oceania (Sydney)", value: "ap-southeast-2" },
];


const formValidationSchema = Yup.object({
    project_name: Yup.string()
        .min(3, "Project name must be at least 3 characters")
        .required("Project name is required"),
    db_password: Yup.string()
        .required("Database password is required")
        .test(
            "is-strong",
            "Password must be at least 8 characters long, contain at least one uppercase letter, one number, and one special character",
            (value) =>
                !!value &&
                value.length >= 8 &&
                /[A-Z]/.test(value) &&  // At least one uppercase letter
                /[a-z]/.test(value) &&  // At least one lowercase letter
                /\d/.test(value) &&     // At least one number
                /[@$!%*?&]/.test(value) // At least one special character
        ),
    region: Yup.string().default("us-east-1")
});


const initialValues: CreateSupabaseProjectValues = {
    project_name: "",
    db_password: "",
    region: "us-east-1"
};

const CreateSupabaseProjectComponent = ({organization_id,onCreate}: createProjectValues) => {

    const dispatch = useAppDispatch();

    const onSubmit = useCallback((values: CreateSupabaseProjectValues, {setSubmitting}: FormikHelpers<CreateSupabaseProjectValues>) => {
        const payload = {
            ...values,
            organization_id:organization_id
        }
        CommonService._project_service.createSupabaseProject(payload)
            .then((response:any) => {
                dispatch(setProjectCode(""))
                if (onCreate) {
                    onCreate(response?.data);
                }
                CommonService.handleMixpanelTrack("Supabse Project created successfully", {
                    project_name: values.project_name,
                    region: values.region,
                    timestamp: new Date().toISOString()
                });
                CommonService._alert.showToast("Project created successfully", "success");
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.detail?.error || "Failed to create project", "error");
                CommonService.handleMixpanelTrack("Supabse Project creation failed", {
                    project_name: values.project_name,
                    region: values.region,
                    error: error?.detail?.error,
                    timestamp: new Date().toISOString()
                });
            })
            .finally(() => setSubmitting(false));
    }, [onCreate,organization_id,dispatch]);

    return (
        <motion.div
            className="create-supabase-project-component"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <Formik
                initialValues={initialValues}
                validationSchema={formValidationSchema}
                onSubmit={onSubmit}
                validateOnChange={true}
                enableReinitialize={true}
                validateOnMount={true}
                validateOnBlur={false}
            >
                {({isSubmitting,values,setFieldValue,isValid}) => (
                    <Form>
                        <Field name="project_name">
                            {(field: any) => (
                                <FormInput
                                    name="project_name"
                                    label="Project Name"
                                    placeholder="Enter project name"
                                    formikField={field}
                                />
                            )}
                        </Field>
                        <Field name="db_password">
                            {(field: any) => (
                                <FormikPasswordInput
                                    name="db_password"
                                    label="Database Password"
                                    type="password"
                                    placeholder="Enter database password"
                                    formikField={field}

                                />
                            )}
                        </Field>
                        <div className="pb-2.5 font-medium text-[16px]">Region</div>
                        <Select value={values.region} onValueChange={(value) => setFieldValue("region", value)}>
                                <SelectTrigger className="w-[250px]">
                                    <SelectValue placeholder="Select a region" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {SUPABASE_REGIONS.map(region => (
                                            <SelectItem key={region.value} value={region.value}>
                                                {region.label}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        <motion.div variants={buttonVariants} whileHover="hover" className={'mt-10'}>
                            <Button loading={isSubmitting} type="submit" disabled={!isValid}>
                                Create Project
                            </Button>
                        </motion.div>
                    </Form>
                )}
            </Formik>
        </motion.div>
    );
};

export default CreateSupabaseProjectComponent;
